import React, { useState } from "react";
import {
  CardComp,
  TableCompData,
  ModalCompAdvanceUpdate,
  ButtonComp,
} from "../../../components";
import { PageTitle } from "../modules/PageHeaders";
import { useGetBirthdayLogsQuery } from "../../../store/admin/automatedBirthdaySlice";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import moment from "moment";
import { convertToTitleCase } from "../../../utils/helpers";

const BirthdayLogs = () => {
  const [page, setPage] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    status: "",
    recipient_type: "",
    search: "",
  });

  const { data, isLoading, isFetching } = useGetBirthdayLogsQuery({
    page,
    ...filters,
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const columns = [
    {
      name: "Date Sent",
      selector: (row) => moment(row.created_at).format("DD-MMM-YYYY HH:mm"),
      sortable: true,
    },
    {
      name: "Recipient",
      selector: (row) => (
        <div>
          <div className="fw-bold">{row.recipient_name}</div>
          <div className="small text-blacklight">{row.recipient_email}</div>
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => (
        <div
          className="rounded-pill px-3 py-1 text-center text-white"
          style={{
            background: row.recipient_type === "member" ? "var(--color-secondary)" : "var(--color-primary)",
            fontSize: "12px",
            minWidth: "80px"
          }}
        >
          {convertToTitleCase(row.recipient_type)}
        </div>
      ),
    },
    {
      name: "Template",
      selector: (row) => row.template_name || "Default",
    },
    {
      name: "Status",
      selector: (row) => (
        <div
          className="rounded-pill px-3 py-1 text-center text-white"
          style={{
            background: row.status === "sent" ? "#19BC31" : "#FF4343",
            fontSize: "12px",
            minWidth: "80px"
          }}
        >
          {convertToTitleCase(row.status)}
        </div>
      ),
    },
    {
      name: "Channels",
      selector: (row) => {
        const channels = Array.isArray(row.channels)
          ? row.channels
          : row.channels?.split(",") || [];
        return (
          <div className="d-flex gap-2">
            {channels.map((channel, i) => (
              <span key={i} className="badge bg-light text-dark border">
                {convertToTitleCase(channel)}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      name: "Action",
      selector: (row) => (
        <ButtonComp
          btnText="View"
          btnClassName="bg-secondary2 text-white rounded-pill px-3 py-1 border-0"
          ButtonInlineStyle={{ fontSize: "12px", minWidth: "60px" }}
          onClick={() => {
            setSelectedLog(row);
            setShowViewModal(true);
          }}
        />
      ),
    },
  ];

  const useModal = [
    {
      name: "View Message Detail",
      component: (
        <div className="p-4">
          <div className="mb-4">
            <h6 className="fw-bold text-blacklight small uppercase tracking-wider mb-2">Recipient</h6>
            <div className="bg-light p-3 rounded-4">
              <p className="mb-1 fw-bold text-black1">{selectedLog?.recipient_name}</p>
              <p className="mb-0 text-blacklight small">{selectedLog?.recipient_email} ({convertToTitleCase(selectedLog?.recipient_type)})</p>
            </div>
          </div>
          
          <div className="mb-4">
            <h6 className="fw-bold text-blacklight small uppercase tracking-wider mb-2">Message Content</h6>
            <div 
              className="bg-white p-4 rounded-4 border shadow-sm"
              style={{ minHeight: "150px", lineHeight: "1.6" }}
              dangerouslySetInnerHTML={{ 
                __html: selectedLog?.content 
                  ? selectedLog.content.replace(/^"|"$/g, '') 
                  : "No content recorded for this message." 
              }}
            />
          </div>

          <div className="row g-3">
            <div className="col-6">
              <h6 className="fw-bold text-blacklight small uppercase tracking-wider mb-2">Status</h6>
              <div 
                className="rounded-pill px-3 py-2 text-center text-white small fw-bold"
                style={{ background: selectedLog?.status === "sent" ? "#19BC31" : "#FF4343" }}
              >
                {convertToTitleCase(selectedLog?.status)}
              </div>
            </div>
            <div className="col-6">
              <h6 className="fw-bold text-blacklight small uppercase tracking-wider mb-2">Delivery Date</h6>
              <div className="bg-light p-2 rounded-pill text-center small text-black1 border">
                {moment(selectedLog?.created_at).format("DD-MMM-YYYY HH:mm")}
              </div>
            </div>
          </div>

          {selectedLog?.error_message && (
            <div className="mt-4 p-3 bg-danger-soft text-danger rounded-4 border border-danger small">
              <p className="mb-0 fw-bold">Note:</p>
              <p className="mb-0">{selectedLog?.error_message}</p>
            </div>
          )}

          <div className="mt-5 d-flex justify-content-center">
            <ButtonComp
              btnText="Close Detail"
              btnClassName="bg-primary1 text-white rounded-pill px-5 py-3 border-0 shadow-sm"
              onClick={() => setShowViewModal(false)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
      <div className="mt-1">
        <PageTitle title="Birthday Message Logs" />
      </div>

      <CardComp cardClassName="mt-4 rounded-4 shadow3 border-0 px-3 py-4">
        {/* Filters Section */}
        <div className="row g-3 mb-4">
          <div className="col-lg-3 col-md-6">
            <label className="small fw-bold mb-1">From Date</label>
            <input
              type="date"
              name="from_date"
              className="form-control bg-white2 border-0"
              value={filters.from_date}
              onChange={handleFilterChange}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="small fw-bold mb-1">To Date</label>
            <input
              type="date"
              name="to_date"
              className="form-control bg-white2 border-0"
              value={filters.to_date}
              onChange={handleFilterChange}
            />
          </div>
          <div className="col-lg-2 col-md-4">
            <label className="small fw-bold mb-1">Recipient Type</label>
            <select
              name="recipient_type"
              className="form-select bg-white2 border-0"
              value={filters.recipient_type}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="member">Members</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div className="col-lg-2 col-md-4">
            <label className="small fw-bold mb-1">Status</label>
            <select
              name="status"
              className="form-select bg-white2 border-0"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Status</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="col-lg-2 col-md-4">
            <label className="small fw-bold mb-1">Search</label>
            <input
              type="text"
              name="search"
              placeholder="Name or email..."
              className="form-control bg-white2 border-0"
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <TableCompData
          columns={columns}
          data={data?.birthday_logs || []}
          progressPending={isLoading || isFetching}
          progressComponent={<TableSkeletonLoader rows={10} columns={6} />}
          pagination
          paginationServer
          paginationTotalRows={data?.pagination?.total || 0}
          onChangePage={(page) => setPage(page)}
        />
      </CardComp>

      <ModalCompAdvanceUpdate
        arrayComp={useModal}
        centered={true}
        pageName={showViewModal ? "View Message Detail" : null}
        handleClose={() => setShowViewModal(false)}
        close={true}
        size="lg"
      />
    </section>
  );
};

export default BirthdayLogs;

